/*
This code was generated by gemini-2.5-pro, see https://codepen.io/plaudit/pen/jEPqbWP
Adapted for ListItemGroup block with custom markup structure
Updated using Claude 4 sonnet in Cursor
*/
class HorizontalScroller {
	// DOM Elements
	scrollerElement: HTMLElement;
	cardArea: HTMLElement;
	cardTrack: HTMLElement;
	scrollLeftButton: HTMLButtonElement;
	scrollRightButton: HTMLButtonElement;
	navigationContainer: HTMLElement;
	cards: HTMLElement[];

	// Properties
	currentTranslateX: number;
	isDragging: boolean;
	dragStartClientX: number;
	currentTrackScroll: number;
	animationFrameId: number | null;
	velocity: number;
	lastPosX: number;
	throwDamping: number;
	minThrowVelocity: number;
	clickDragThreshold: number;
	justDragged: boolean;
	cardWidthPlusGap: number;

	// Continuous scroll properties
	continuousScrollActive: boolean;
	continuousScrollDirection: number;
	continuousScrollFrameId: number | null;
	scrollStep: number;

	// Bound event handlers
	boundHandleDragMove: (event: MouseEvent) => void;
	boundHandleDragEnd: () => void;
	boundHandleTouchMove: (event: TouchEvent) => void;
	boundHandleTouchEnd: () => void;
	boundHandleResize: () => void;
	boundHandleCardKeyboardNavigation: (event: KeyboardEvent) => void;
	boundHandleWheel: (event: WheelEvent) => void;

	constructor(element: HTMLElement) {
		this.scrollerElement = element;
		
		// Find the existing elements based on ListItemGroup structure
		this.cardTrack = element.querySelector('.listItemGroup__items') as HTMLElement;
		
		if (!this.cardTrack) {
			console.error('ListItemGroup items container not found within:', element);
			return;
		}

		// Create navigation arrows and container
		this._createNavigationControls();
		
		// Set up card area (the items container becomes both track and area)
		this.cardArea = this.cardTrack;
		
		// Get all list items as cards
		this.cards = Array.from(this.cardTrack.children) as HTMLElement[];
		if (this.cards.length === 0) {
			console.warn('No items found in ListItemGroup:', element);
			this._hideNavigationControls();
			return;
		}

		// --- Properties ---
		this.currentTranslateX = 0;
		this.isDragging = false;
		this.dragStartClientX = 0;
		this.currentTrackScroll = 0;
		this.animationFrameId = null;
		this.velocity = 0;
		this.lastPosX = 0;
		this.throwDamping = 0.92;
		this.minThrowVelocity = 0.5;
		this.clickDragThreshold = 10;
		this.justDragged = false;
		this.cardWidthPlusGap = 0;

		// Continuous scroll properties
		this.continuousScrollActive = false;
		this.continuousScrollDirection = 0;
		this.continuousScrollFrameId = null;
		this.scrollStep = 10;

		// Bind methods that will be used as event handlers or need 'this' context
		this.boundHandleDragMove = this._handleDragMove.bind(this);
		this.boundHandleDragEnd = this._handleDragEnd.bind(this);
		this.boundHandleTouchMove = this._handleTouchMove.bind(this);
		this.boundHandleTouchEnd = this._handleTouchEnd.bind(this);
		this.boundHandleResize = this._handleResize.bind(this);
		this.boundHandleCardKeyboardNavigation = this._handleCardKeyboardNavigation.bind(this);
		this.boundHandleWheel = this._handleWheel.bind(this);

		this._init();
	}

	_createNavigationControls() {
		// Create navigation container
		this.navigationContainer = document.createElement('div');
		this.navigationContainer.className = 'carousel-controls';
		
		// Create left arrow button
		this.scrollLeftButton = document.createElement('button');
		this.scrollLeftButton.className = 'carousel-control';
		this.scrollLeftButton.setAttribute('data-scroll-button', 'left');
		this.scrollLeftButton.setAttribute('aria-label', 'Scroll left');
		this.scrollLeftButton.innerHTML = `
			<span class="sr-only">Previous items</span>
			<svg xmlns="http://www.w3.org/2000/svg" width="11" height="16" viewBox="0 0 11 16" fill="none">
				<path d="M8.45095 0.0726803L0.733155 8.03536L8.45095 15.998L10.3632 14.0252L4.55757 8.03536L10.3632 2.04556L8.45095 0.0726803Z" fill="white"/>
			</svg>
		`;
		
		// Create right arrow button
		this.scrollRightButton = document.createElement('button');
		this.scrollRightButton.className = 'carousel-control';
		this.scrollRightButton.setAttribute('data-scroll-button', 'right');
		this.scrollRightButton.setAttribute('aria-label', 'Scroll right');
		this.scrollRightButton.innerHTML = `
			<span class="sr-only">Next items</span>
			<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
				<path d="M2.15073 15.9981L9.86853 8.03544L2.15073 0.0727539L0.238525 2.04563L6.04412 8.03544L0.238525 14.0252L2.15073 15.9981Z" fill="white"/>
			</svg>
		`;

		// Add buttons to navigation container
		this.navigationContainer.appendChild(this.scrollLeftButton);
		this.navigationContainer.appendChild(this.scrollRightButton);
		
		// Insert navigation controls before the items container
		this.scrollerElement.insertBefore(this.navigationContainer, this.cardTrack);
	}

	_hideNavigationControls() {
		if (this.navigationContainer) {
			this.navigationContainer.style.display = 'none';
		}
	}

	_init() {
		this._setupCarouselStyles();
		this._calculateDimensions();
		this._addEventListeners();
		this.scrollTo(0, false); // Initial position and button state update
	}

	_setupCarouselStyles() {
		// Add necessary classes for carousel functionality
		this.cardTrack.classList.add('carousel-track');
		
		// Create wrapper div for horizontal scrolling if needed
		const cardAreaWrapper = document.createElement('div');
		cardAreaWrapper.className = 'carousel-area';
		cardAreaWrapper.setAttribute('data-card-area', '');
		
		// Wrap the track
		this.cardTrack.parentNode?.insertBefore(cardAreaWrapper, this.cardTrack);
		cardAreaWrapper.appendChild(this.cardTrack);
		
		// Update cardArea reference
		this.cardArea = cardAreaWrapper;
		
		// Set track as data-card-track for consistency
		this.cardTrack.setAttribute('data-card-track', '');
	}

	_calculateDimensions() {
		if (this.cards.length > 0) {
			const cardStyle = window.getComputedStyle(this.cards[0]);
			const trackGap = parseFloat(window.getComputedStyle(this.cardTrack).gap) || 20;
			this.cardWidthPlusGap = this.cards[0].offsetWidth + (this.cards.length > 1 ? trackGap : 0);
		} else {
			this.cardWidthPlusGap = 0;
		}
	}

	_addEventListeners() {
		this.scrollLeftButton.addEventListener('click', () => this._handleArrowClick(1));
		this.scrollRightButton.addEventListener('click', () => this._handleArrowClick(-1));

		// Continuous scroll for arrow buttons
		[this.scrollLeftButton, this.scrollRightButton].forEach(button => {
			const direction = button === this.scrollLeftButton ? 1 : -1;
			button.addEventListener('mousedown', (e: MouseEvent) => { if (e.button === 0) this._startContinuousScroll(direction); });
			button.addEventListener('mouseup', () => this._stopContinuousScroll());
			button.addEventListener('mouseleave', () => this._stopContinuousScroll());
			button.addEventListener('touchstart', (e: TouchEvent) => { e.preventDefault(); this._startContinuousScroll(direction); }, { passive: false });
			button.addEventListener('touchend', () => this._stopContinuousScroll());
			button.addEventListener('touchcancel', () => this._stopContinuousScroll());
		});

		// Dragging
		this.cardTrack.addEventListener('mousedown', this._handleMouseDown.bind(this));
		this.cardTrack.addEventListener('touchstart', this._handleTouchStart.bind(this), { passive: true });

		// Card activation and keyboard navigation
		this.cards.forEach(card => {
			card.setAttribute('tabindex', '0');
			card.setAttribute('role', 'button');
			
			card.addEventListener('click', (e: MouseEvent) => this._handleCardActivation(card, e));
			card.addEventListener('keydown', (e: KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault(); // Prevent page scroll on Space
					this._handleCardActivation(card, e);
				}
			});
		});
		
		// Keyboard navigation between cards (left/right arrows)
		this.cardArea.addEventListener('keydown', this.boundHandleCardKeyboardNavigation);

		// Horizontal scroll wheel support
		this.cardArea.addEventListener('wheel', this.boundHandleWheel, { passive: false });

		window.addEventListener('resize', this.boundHandleResize);
	}

	_applyTransform(translateX: number, smooth = false) {
		this.cardTrack.style.transition = smooth ? 'transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)' : 'none';
		this.cardTrack.style.transform = `translateX(${translateX}px)`;
	}

	scrollTo(newScrollPosition: number, smooth = false) {
		const maxScroll = 0;
		// Ensure cardArea.clientWidth is positive to avoid NaN or issues if not rendered
		const areaWidth = this.cardArea.clientWidth || 0;
		const trackWidth = this.cardTrack.scrollWidth || 0;

		let minScroll = -(trackWidth - areaWidth);
		if (trackWidth <= areaWidth) { // If content is not wider than container
			minScroll = 0; // No scrolling needed, minScroll is 0
		}

		this.currentTranslateX = Math.max(minScroll, Math.min(maxScroll, newScrollPosition));
		if (trackWidth <= areaWidth) {
			this.currentTranslateX = 0; // Force to 0 if no overflow
		}

		this._applyTransform(this.currentTranslateX, smooth);
		this._updateButtonStates();
	}

	_updateButtonStates() {
		const areaWidth = this.cardArea.clientWidth || 0;
		const trackWidth = this.cardTrack.scrollWidth || 0;
		const maxScroll = 0;
		const minScroll = -(trackWidth - areaWidth);

		this.scrollLeftButton.disabled = this.currentTranslateX >= maxScroll;
		this.scrollRightButton.disabled = this.currentTranslateX <= minScroll || trackWidth <= areaWidth;

		this.scrollLeftButton.setAttribute('aria-disabled', this.scrollLeftButton.disabled.toString());
		this.scrollRightButton.setAttribute('aria-disabled', this.scrollRightButton.disabled.toString());
	}

	_handleArrowClick(directionFactor: number) { // +1 for left (scroll content right), -1 for right (scroll content left)
		// Calculate scroll distance based on screen size
		const isMobile = window.innerWidth <= 768;
		let scrollDistance;
		
		if (isMobile) {
			// On mobile, use the carousel area width (which should match one card)
			// This ensures we scroll exactly one full card at a time
			scrollDistance = this.cardArea.clientWidth;
			
			// Debug logging for mobile
			console.log('Mobile scroll - using cardArea width:', scrollDistance, 'viewport width:', window.innerWidth);
		} else {
			// On desktop, use the calculated card width plus gap
			scrollDistance = this.cardWidthPlusGap;
		}
		
		this.scrollTo(this.currentTranslateX + directionFactor * scrollDistance, true);
	}

	_startContinuousScroll(directionFactor: number) {
		if (this.continuousScrollActive) return;
		this.continuousScrollActive = true;
		this.continuousScrollDirection = directionFactor;

		// Clear any previous throw animation
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
		this.velocity = 0;

		const scrollLoop = () => {
			if (!this.continuousScrollActive) return;

			const newScroll = this.currentTranslateX + (this.continuousScrollDirection * this.scrollStep);
			this.scrollTo(newScroll, false);

			const areaWidth = this.cardArea.clientWidth || 0;
			const trackWidth = this.cardTrack.scrollWidth || 0;

			if ((this.continuousScrollDirection === 1 && this.currentTranslateX >= 0) ||
				(this.continuousScrollDirection === -1 && (this.currentTranslateX <= -(trackWidth - areaWidth) || trackWidth <= areaWidth))) {
				this._stopContinuousScroll();
			} else {
				this.continuousScrollFrameId = requestAnimationFrame(scrollLoop);
			}
		};
		this.continuousScrollFrameId = requestAnimationFrame(scrollLoop);
	}

	_stopContinuousScroll() {
		this.continuousScrollActive = false;
		if (this.continuousScrollFrameId) {
			cancelAnimationFrame(this.continuousScrollFrameId);
			this.continuousScrollFrameId = null;
		}
		// After continuous scroll, re-apply smooth transition for potential throw/snap
		if (!this.isDragging) {
			// No throw after button scroll, just ensure state is correct
			this._updateButtonStates();
		}
	}

	_handleMouseDown(e: MouseEvent) {
		if (e.button !== 0) return; // Only left click
		this.isDragging = true;
		this.dragStartClientX = e.clientX;
		this.currentTrackScroll = this.currentTranslateX;
		this.cardTrack.classList.add('dragging');
		if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
		this.velocity = 0;
		this.lastPosX = e.clientX;
		this.justDragged = false; // Reset for click detection
		e.preventDefault();

		document.addEventListener('mousemove', this.boundHandleDragMove);
		document.addEventListener('mouseup', this.boundHandleDragEnd);
	}

	_handleTouchStart(e: TouchEvent) {
		this.isDragging = true;
		this.dragStartClientX = e.touches[0].clientX;
		this.currentTrackScroll = this.currentTranslateX;
		this.cardTrack.classList.add('dragging');
		if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
		this.velocity = 0;
		this.lastPosX = e.touches[0].clientX;
		this.justDragged = false;

		// Add touchmove and touchend listeners to document to capture movement outside the track
		document.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false });
		document.addEventListener('touchend', this.boundHandleTouchEnd);
		document.addEventListener('touchcancel', this.boundHandleTouchEnd); // Also treat cancel as end
	}

	_handleDragMove(e: MouseEvent) {
		if (!this.isDragging) return;
		const currentPosX = e.clientX;
		this._updateDrag(currentPosX);
	}

	_handleTouchMove(e: TouchEvent) {
		if (!this.isDragging || !e.touches[0]) return;
		e.preventDefault(); // Prevent page scroll while dragging track
		const currentPosX = e.touches[0].clientX;
		this._updateDrag(currentPosX);
	}

	_updateDrag(currentPosX: number) {
		const deltaX = currentPosX - this.dragStartClientX;
		const moveX = currentPosX - this.lastPosX;
		this.velocity = moveX;
		this.lastPosX = currentPosX;
		this.scrollTo(this.currentTrackScroll + deltaX, false);
	}

	_handleDragEnd() {
		if (!this.isDragging) return;
		this.isDragging = false;
		this.cardTrack.classList.remove('dragging');
		document.removeEventListener('mousemove', this.boundHandleDragMove);
		document.removeEventListener('mouseup', this.boundHandleDragEnd);

		if (Math.abs(this.lastPosX - this.dragStartClientX) > this.clickDragThreshold) {
			this.justDragged = true; // Mark that a drag occurred
		}
		this._applyThrow();
		// Reset justDragged after a very short delay to allow click event to process
		setTimeout(() => { this.justDragged = false; }, 0);
	}

	_handleTouchEnd() {
		if (!this.isDragging) return;
		this.isDragging = false;
		this.cardTrack.classList.remove('dragging');
		document.removeEventListener('touchmove', this.boundHandleTouchMove);
		document.removeEventListener('touchend', this.boundHandleTouchEnd);
		document.removeEventListener('touchcancel', this.boundHandleTouchEnd);

		if (Math.abs(this.lastPosX - this.dragStartClientX) > this.clickDragThreshold) {
			this.justDragged = true;
		}
		this._applyThrow();
		setTimeout(() => { this.justDragged = false; }, 0);
	}


	_applyThrow() {
		if (Math.abs(this.velocity) < this.minThrowVelocity || this.cardTrack.scrollWidth <= this.cardArea.clientWidth) {
			this.velocity = 0;
			this._updateButtonStates(); // Ensure buttons are correct
			return;
		}

		const throwAnimation = () => {
			if (!this.isDragging && Math.abs(this.velocity) > this.minThrowVelocity) {
				this.currentTranslateX += this.velocity;
				this.velocity *= this.throwDamping;

				const maxScroll = 0;
				const minScroll = -(this.cardTrack.scrollWidth - this.cardArea.clientWidth);

				if (this.currentTranslateX > maxScroll) {
					this.currentTranslateX = maxScroll; this.velocity = 0;
				} else if (this.currentTranslateX < minScroll) {
					this.currentTranslateX = minScroll; this.velocity = 0;
				}

				this.scrollTo(this.currentTranslateX, false);
				this.animationFrameId = requestAnimationFrame(throwAnimation);
			} else {
				this.velocity = 0;
				if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
				this.animationFrameId = null;
				this._updateButtonStates();
			}
		};
		this.animationFrameId = requestAnimationFrame(throwAnimation);
	}

	_handleCardActivation(card: HTMLElement, event: MouseEvent | KeyboardEvent) {
		if (event.type === 'click' && this.justDragged) {
			event.preventDefault(); // Prevent anchor navigation if it was a drag
			return;
		}

		// Look for primary action elements in ListItemGroup structure
		const primaryActionElement = card.querySelector('.item__heading a, .wp-block-button__link');
		if (primaryActionElement) {
			// If the event target is already the primary action, let it proceed.
			// Otherwise, if the card itself was clicked/activated by keyboard, trigger the primary action.
			if (event.target !== primaryActionElement && event.currentTarget === card) {
				console.log(`Item "${card.querySelector('.item__heading')?.textContent?.trim()}" activated, triggering primary action.`);
				(primaryActionElement as HTMLElement).click();
			} else if (event.target === primaryActionElement) {
				console.log(`Primary action on "${card.querySelector('.item__heading')?.textContent?.trim()}" directly triggered.`);
				// If it's an anchor with href="#", prevent default to avoid jump to top
				if ((primaryActionElement as HTMLAnchorElement).tagName === 'A' && (primaryActionElement as HTMLAnchorElement).getAttribute('href') === '#') {
					event.preventDefault();
					console.log("Default action for # href prevented on anchor.");
				}
			}
		} else {
			console.log(`Item "${card.querySelector('.item__heading')?.textContent?.trim()}" activated, no primary action found.`);
		}
	}

	_handleCardKeyboardNavigation(event: KeyboardEvent) {
		const focusedElement = document.activeElement as HTMLElement;
		if (!this.cards.includes(focusedElement)) return;

		let currentCardIndex = this.cards.indexOf(focusedElement);

		if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
			event.preventDefault();
			const direction = event.key === 'ArrowRight' ? 1 : -1;
			const nextCardIndex = Math.min(this.cards.length - 1, Math.max(0, currentCardIndex + direction));

			if (nextCardIndex !== currentCardIndex) {
				this.cards[nextCardIndex].focus();
				// Scroll card into view if necessary
				const cardRect = this.cards[nextCardIndex].getBoundingClientRect();
				const areaRect = this.cardArea.getBoundingClientRect();
				const gap = parseFloat(window.getComputedStyle(this.cardTrack).gap) || 0;

				if (cardRect.right > areaRect.right - gap) { // Check right boundary
					this.scrollTo(this.currentTranslateX - (cardRect.right - areaRect.right + gap), true);
				} else if (cardRect.left < areaRect.left + gap) { // Check left boundary
					this.scrollTo(this.currentTranslateX + (areaRect.left - cardRect.left + gap), true);
				}
			}
		}
	}

	_handleResize() {
		this._calculateDimensions();
		// Recalculate scroll position to prevent overflow issues after resize
		this.scrollTo(this.currentTranslateX, false);
	}

	_handleWheel(event: WheelEvent) {
		// Check if it's a horizontal scroll gesture or shift+vertical scroll
		const isHorizontalScroll = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
		
		if (!isHorizontalScroll) return; // Let normal vertical scrolling work
		
		// Prevent default scrolling behavior for horizontal gestures
		event.preventDefault();
		
		// Determine scroll direction and amount
		let scrollDelta = 0;
		if (event.shiftKey && event.deltaY !== 0) {
			// Shift + vertical wheel becomes horizontal scroll
			scrollDelta = event.deltaY;
		} else if (event.deltaX !== 0) {
			// Direct horizontal scroll
			scrollDelta = event.deltaX;
		}
		
		// Apply a multiplier to make scrolling feel responsive but not too fast
		const scrollMultiplier = 2;
		const scrollAmount = scrollDelta * scrollMultiplier;
		
		// Update scroll position (negative delta moves content right, positive moves left)
		this.scrollTo(this.currentTranslateX - scrollAmount, false);
	}

	destroy() {
		// Remove all event listeners to prevent memory leaks if the element is removed
		window.removeEventListener('resize', this.boundHandleResize);
		document.removeEventListener('mousemove', this.boundHandleDragMove);
		document.removeEventListener('mouseup', this.boundHandleDragEnd);
		document.removeEventListener('touchmove', this.boundHandleTouchMove);
		document.removeEventListener('touchend', this.boundHandleTouchEnd);
		document.removeEventListener('touchcancel', this.boundHandleTouchEnd);
		this.cardArea.removeEventListener('keydown', this.boundHandleCardKeyboardNavigation);
		this.cardArea.removeEventListener('wheel', this.boundHandleWheel);

		// Further cleanup like removing listeners from buttons, cards, etc.
		// For simplicity, not all individual element listeners are removed here,
		// but in a complex app, this would be more thorough.

		if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
		if (this.continuousScrollFrameId) cancelAnimationFrame(this.continuousScrollFrameId);

		// Remove initialization class
		this.scrollerElement.classList.remove('carousel-initialized');
		
		console.log('HorizontalScroller instance destroyed for:', this.scrollerElement);
	}
}

// --- Auto-initialization with MutationObserver ---
document.addEventListener('DOMContentLoaded', () => {
	const initScrollerInstance = (elementNode: Node) => {
		// Check if it's an element, has the carousel style, and not already initialized
		if (elementNode.nodeType === Node.ELEMENT_NODE &&
			(elementNode as HTMLElement).matches &&
			(elementNode as HTMLElement).matches('.listItemGroup[data-style="caro"]') &&
			!(elementNode as HTMLElement).classList.contains('carousel-initialized')) {
			new HorizontalScroller(elementNode as HTMLElement);
			(elementNode as HTMLElement).classList.add('carousel-initialized');
		}
	};

	const initAllScrollersInContainer = (container: HTMLElement | Document) => {
		const scrollerElements = container.querySelectorAll<HTMLElement>('.listItemGroup[data-style="caro"]:not(.carousel-initialized)');
		scrollerElements.forEach(el => {
			// Check again in case the element was already processed by a higher-level init
			if (!el.classList.contains('carousel-initialized')) {
				new HorizontalScroller(el);
				el.classList.add('carousel-initialized');
			}
		});
	};

	// Initial check for carousel ListItemGroups already in the DOM
	initAllScrollersInContainer(document.body);

	// Observe for carousel ListItemGroups added dynamically
	const observer = new MutationObserver((mutationsList: MutationRecord[]) => {
		for (const mutation of mutationsList) {
			if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
				mutation.addedNodes.forEach(node => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						// Check if the added node itself is a carousel
						initScrollerInstance(node);
						// Check if children of the added node are carousels (if a wrapper was added)
						// This ensures nested initializations are caught if a large chunk of HTML is added
						if ((node as HTMLElement).querySelectorAll) { // Ensure it's an element that can have querySelectorAll
							initAllScrollersInContainer(node as HTMLElement);
						}
					}
				});
			}
		}
	});
	observer.observe(document.body, { childList: true, subtree: true });
});
